import axios, { AxiosError, AxiosRequestConfig } from "axios";
import AuthenticationService from "./AuthenticationService";
import {
  IAuthenticationData,
  AUTHENTICATION_DATA_STORAGE_KEY
} from "../pages/shared/AuthenticationContext";
import { getCache } from "../utils/PersistenceUtils";
import ErrorLoggingService from "./ErrorLoggingService";
import ApiCallTracker from "../utils/ApiCallTracker";
import { extractDownloadFilename } from "../utils/FormatUtils";

const baseUrl =
  process.env.REACT_APP_INTIZA_COMPANY_AUTH ??
  process.env.REACT_APP_INTIZA_COMPANY_AUTH + "";

const baseUrlAssistance =
  process.env.REACT_APP_INTIZA_ASSISTANCE_URL ?? baseUrl;
type TData = {};
type RequestPromiseExtraProperties = {
  abortController: AbortController;
  endpoint: string;
};
export type RequestPromise<TResponse> = Promise<
  TResponse | AxiosError<TResponse, TData>
> &
  RequestPromiseExtraProperties;
class RequestService {
  public static getBaseUrl() {
    return baseUrl;
  }

  public static getBaseUrlAssistance() {
    return baseUrlAssistance;
  }

  public static currentAbortController = new AbortController();

  public static post<TResponse = {}>(
    endpoint: string,
    data: TData,
    withAuthorization = true,
    requestBaseUrl: string = baseUrl,
    timeout?: number
  ): Promise<TResponse | AxiosError<TResponse, TData>> {
    return RequestService.makeRequest<TResponse>(
      endpoint,
      data,
      withAuthorization,
      requestBaseUrl,
      "post",
      timeout
    );
  }
  public static get<TResponse = {}>(
    endpoint: string,
    data: TData,
    withAuthorization = true,
    requestBaseUrl: string = baseUrl,
    timeout?: number
  ): Promise<TResponse | AxiosError<TResponse, TData>> {
    return RequestService.makeRequest<TResponse>(
      endpoint,
      data,
      withAuthorization,
      requestBaseUrl,
      "get",
      timeout
    );
  }
  public static delete<TResponse = {}>(
    endpoint: string,
    data: TData,
    withAuthorization = true,
    requestBaseUrl: string = baseUrl,
    timeout?: number
  ): Promise<TResponse | AxiosError<TResponse, TData>> {
    return RequestService.makeRequest<TResponse>(
      endpoint,
      data,
      withAuthorization,
      requestBaseUrl,
      "delete",
      timeout
    );
  }
  public static patch<TResponse = {}>(
    endpoint: string,
    data: TData,
    withAuthorization = true,
    requestBaseUrl: string = baseUrl,
    timeout?: number
  ): Promise<TResponse | AxiosError<TResponse, TData>> {
    return RequestService.makeRequest<TResponse>(
      endpoint,
      data,
      withAuthorization,
      requestBaseUrl,
      "patch",
      timeout
    );
  }
  public static makeRequest<TResponse = {}>(
    endpoint: string,
    data: TData,
    withAuthorization = true,
    requestBaseUrl: string = baseUrl,
    method: "post" | "get" | "delete" | "patch" = "post",
    timeout?: number
  ): Promise<TResponse | AxiosError<TResponse, TData>> {
    const headers: { Authorization?: string; "Content-Type": string } = {
      "Content-Type": "application/json"
    };

    const abortController = new AbortController();
    RequestService.currentAbortController = abortController;
    const authPromise = withAuthorization
      ? AuthenticationService.getAuthenticationToken().then(
          (authenticationToken) => {
            if (!authenticationToken) {
              window.location.pathname = "/";
              return Error("no auth token");
            }
            headers.Authorization = "Bearer " + authenticationToken;
          }
        )
      : Promise.resolve();
    const axiosRequest = authPromise.then(async () => {
      const makeRequestWithRetry = async (
        isRetry: boolean = false
      ): Promise<TResponse | AxiosError<TResponse, TData>> => {
        const config: AxiosRequestConfig<TData> = {
          method: method,
          timeout: timeout ?? 30000,
          url: requestBaseUrl + endpoint,
          headers,
          data,
          signal: abortController.signal
        };

        try {
          ApiCallTracker.track(endpoint);
          const response = await axios(config);
          const processedData = await this.refreshList(response.data);
          const interceptions =
            RequestService.interceptList[endpoint]?.splice(0, 9999) ?? [];
          interceptions.forEach((inter) => inter(response));
          return processedData as TResponse;
        } catch (error) {
          const axiosError = error as AxiosError<TResponse, TData>;

          // Interceptor 401: refrescar token y reintentar una vez
          if (
            axiosError.response?.status === 401 &&
            withAuthorization &&
            !isRetry
          ) {
            // Intentar refrescar el token
            const authenticationData = getCache<IAuthenticationData>(
              AUTHENTICATION_DATA_STORAGE_KEY
            );
            if (authenticationData) {
              const refreshedToken = await AuthenticationService.refreshToken({
                refreshToken: authenticationData
              });

              if (refreshedToken) {
                // Actualizar headers con el nuevo token
                headers.Authorization = "Bearer " + refreshedToken;
                // Reintentar la petición original una vez
                return makeRequestWithRetry(true);
              }
            }
          }

          // Log request errors (excepto si es un retry que falló, para evitar duplicados)
          if (!isRetry) {
            ErrorLoggingService.logRequestError(
              axiosError,
              requestBaseUrl + endpoint,
              data
            );
          }

          return axiosError;
        }
      };

      return makeRequestWithRetry();
    });
    const request = Object.assign(axiosRequest, {
      abortController,
      endpoint
    }) as RequestPromise<TResponse>;
    return request;
  }

  public static async togglefilteruseridpaused(value: boolean) {
    const authenticationData = getCache<IAuthenticationData>(
      AUTHENTICATION_DATA_STORAGE_KEY
    );
    if (!authenticationData) {
      return;
    }
    await AuthenticationService.refreshToken({
      refreshToken: authenticationData,
      filteruseridpaused: Number(value) as 0 | 1
    });
  }

  private static async refreshList<TResponse extends Record<string, unknown>>(
    data: TResponse
  ) {
    if (data && typeof data === "object" && "refreshlists" in data) {
      const authenticationData = getCache<IAuthenticationData>(
        AUTHENTICATION_DATA_STORAGE_KEY
      );
      if (!authenticationData) {
        return;
      }
      await AuthenticationService.refreshToken({
        refreshToken: authenticationData,
        refreshlists: 1
      });
    }
    return data;
  }

  public static async downloadFile(
    fileDownloadUrl: string,
    fileName = "export.xlsx",
    requestBaseUrl = baseUrl
  ): Promise<Error | unknown> {
    const headers: { Authorization?: string; "Content-Type": string } = {
      "Content-Type": "application/json"
    };
    const authenticationToken =
      await AuthenticationService.getAuthenticationToken();
    if (authenticationToken === null) {
      window.location.pathname = "/";
      return Error("no auth token");
    }
    headers.Authorization = "Bearer " + authenticationToken;
    try {
      const response = await axios({
        url: requestBaseUrl + fileDownloadUrl,
        method: "GET",
        responseType: "blob",
        headers
      });
      const name = extractDownloadFilename(
        response.headers["content-disposition"],
        fileName
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", name);
      document.body.appendChild(link);
      link.click();
      if (link && typeof link.remove === "function") {
        try {
          link.remove();
        } catch (e) {
          console.warn("No se pudo remover el enlace de descarga del DOM:", e);
        }
      }
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // Log download errors
      if (error instanceof Error) {
        ErrorLoggingService.logError(error, requestBaseUrl + fileDownloadUrl, {
          fileName
        });
      }
      return Error("status error");
    }
  }
  public static async downloadFilePost(
    fileDownloadUrl: string,
    fileId: string,
    fileName = "export.xlsx"
  ): Promise<Error | unknown> {
    const headers: { Authorization?: string; "Content-Type": string } = {
      "Content-Type": "application/json"
    };
    const authenticationToken =
      await AuthenticationService.getAuthenticationToken();
    if (authenticationToken === null) {
      window.location.pathname = "/";
      return Error("no auth token");
    }
    headers.Authorization = "Bearer " + authenticationToken;
    try {
      const response = await axios({
        url: baseUrl + fileDownloadUrl,
        method: "POST",
        responseType: "blob",
        headers
      });
      const name = extractDownloadFilename(
        response.headers["content-disposition"],
        fileName
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", name);
      document.body.appendChild(link);
      link.click();
      if (link && typeof link.remove === "function") {
        try {
          link.remove();
        } catch (e) {
          console.warn("No se pudo remover el enlace de descarga del DOM:", e);
        }
      }
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // Log download errors
      if (error instanceof Error) {
        ErrorLoggingService.logError(error, baseUrl + fileDownloadUrl, {
          fileName
        });
      }
      return Error("status error");
    }
  }
  public static async downloadFilePostWithPayload(
    fileDownloadUrl: string,
    payload: any,
    fileName = "export.xlsx"
  ): Promise<Error | unknown> {
    const headers: { Authorization?: string; "Content-Type": string } = {
      "Content-Type": "application/json"
    };
    const authenticationToken =
      await AuthenticationService.getAuthenticationToken();
    if (authenticationToken === null) {
      window.location.pathname = "/";
      return Error("no auth token");
    }
    headers.Authorization = "Bearer " + authenticationToken;
    try {
      const response = await axios({
        url: baseUrl + fileDownloadUrl,
        method: "POST",
        responseType: "blob",
        headers,
        data: payload
      });
      const name = extractDownloadFilename(
        response.headers["content-disposition"],
        fileName
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", name);
      document.body.appendChild(link);
      link.click();
      if (link && typeof link.remove === "function") {
        try {
          link.remove();
        } catch (e) {
          console.warn("No se pudo remover el enlace de descarga del DOM:", e);
        }
      }
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // Log download errors
      if (error instanceof Error) {
        ErrorLoggingService.logError(error, baseUrl + fileDownloadUrl, {
          fileName
        });
      }
      return Error("status error");
    }
  }
  private static interceptList: Record<string, ((result: any) => void)[]> = {};
  static intercept(
    path: string,
    func: (typeof RequestService.interceptList)[string][number]
  ) {
    this.interceptList[path] = this.interceptList[path] ?? [];
    this.interceptList[path].push(func);
  }
}

// used for testing purposes :)
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export default RequestService;
